import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    companyName: '',
    billingEmail: '',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function update(key: keyof typeof form, value: string) {
    setForm((f) => ({
      ...f,
      [key]: value,
      // Default the billing address to the login address until edited
      ...(key === 'email' && f.billingEmail === f.email ? { billingEmail: value } : {}),
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (form.password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setSubmitting(true);
    try {
      await register({
        companyName: form.companyName,
        billingEmail: form.billingEmail || form.email,
        name: form.name,
        email: form.email,
        password: form.password,
      });
      navigate('/');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Registrierung fehlgeschlagen.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const fields: { key: keyof typeof form; label: string; type?: string }[] = [
    { key: 'companyName', label: 'Firma' },
    { key: 'name', label: 'Ihr Name' },
    { key: 'email', label: 'E-Mail (Login)', type: 'email' },
    { key: 'billingEmail', label: 'Rechnungs-E-Mail', type: 'email' },
    { key: 'password', label: 'Passwort', type: 'password' },
    { key: 'confirmPassword', label: 'Passwort bestätigen', type: 'password' },
  ];

  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm py-8">
        <img src="/juni-logo.svg" alt="juni" className="h-8 mx-auto mb-8" />
        <div className="bg-surface border border-slate-200 rounded-xl shadow-sm p-6">
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Konto erstellen</h1>
          <p className="text-sm text-slate-500 mb-5">Kostenlos registrieren und direkt loslegen.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}
            {fields.map(({ key, label, type }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                <input
                  type={type ?? 'text'}
                  required
                  value={form[key]}
                  onChange={(e) => update(key, e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            ))}
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm py-2.5 rounded-lg transition-colors disabled:opacity-60"
            >
              {submitting ? 'Konto wird erstellt…' : 'Konto erstellen'}
            </button>
          </form>

          <p className="text-sm text-slate-500 mt-4 text-center">
            Bereits registriert?{' '}
            <Link to="/login" className="text-brand-600 hover:text-brand-800">
              Anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
